<template>
    <div v-if="isLoading || !src" class="d-flex flex-column align-center justify-center py-12 px-8">
        <v-progress-linear color="primary" class="mr-4 mt-6" :value="progress.value / progress.total * 100" />
        <br/>
        Loading PDF preview...
    </div>
    <div ref="viewer" class="q-pdf-viewer" v-else>
        <div 
            v-for="n in numberOfPages"
            :key="n"
            class="q-pdf-viewer__page-wrapper"
        >
            <div class="q-pdf-viewer__page">
                <VuePdf :page="n" :src="src" @page-size="onPageSize" />
            </div>
            <div v-if="numberOfPages > 1" class="q-pdf-viewer__page-info">
                {{ n }} / {{ numberOfPages }}
            </div>       
        </div>
    </div>
</template>

<script>
import VuePdf from 'vue-pdf'

// Make sure to v-if this component. Idk why, but some time pdfjs doesn't make a request.
export default {
    components: { VuePdf },

    props: {
        value: { type: String, required: true }
    },

    data() {
        return {
            isLoading: false,
            src: null,
            numberOfPages: 0,
            progress: {
                value: 0,
                total: 0
            }
        }
    },

    methods: {
        loadPdf(src) {
            /**
             * Use createLoadingTask to load all pages of the PDF
             * @see https://github.com/FranckFreiburger/vue-pdf#example---display-multiple-pages-of-the-same-pdf-document
             */
            this.isLoading = true;
            this.progress.value = 0
            this.src = VuePdf.createLoadingTask(src, {
                onProgress: ({ loaded, total }) => {
                    this.progress.value = loaded
                    this.progress.total = total
                }
            });
            this.src.promise.then((pdf) => {
                this.isLoading = false;
                this.numberOfPages = pdf.numPages;
                this.$nextTick(() => {
                    this.observeViewer()
                    this.syncAnnotationLayers()
                })
            })
        },

        onPageSize() {
            // Canvas CSS height is set in vue-pdf's page-size handler, we sync after that.
            // Annotation children arrive later — MutationObserver re-syncs when they do.
            this.$nextTick(() => {
                this.syncAnnotationLayers()
                this.watchAnnotationLayers()
            })
        },

        /**
         * vue-pdf renders the canvas at devicePixelRatio but CSS-stretches it to 100% width.
         * Annotation link hit-boxes are laid out in canvas pixel space.
         * We scale them to match the displayed size.
         */
        syncAnnotationLayers() {
            const viewer = this.$refs.viewer
            if (!viewer) {
                return
            }

            viewer.querySelectorAll('.q-pdf-viewer__page').forEach((pageEl) => {
                const canvas = pageEl.querySelector('canvas')
                const layer = pageEl.querySelector('.annotationLayer')
                if (!canvas || !layer || !canvas.width) {
                    return
                }

                const scale = canvas.offsetWidth / canvas.width
                layer.style.transformOrigin = '0 0'
                layer.style.transform = `scale(${scale})`
            })
        },

        watchAnnotationLayers() {
            const viewer = this.$refs.viewer
            if (!viewer || typeof MutationObserver === 'undefined') {
                return
            }

            this.disconnectAnnotationObservers()
            this._annotationObservers = []

            viewer.querySelectorAll('.annotationLayer').forEach((layer) => {
                const observer = new MutationObserver(() => {
                    this.syncAnnotationLayers()
                })
                observer.observe(layer, { childList: true })
                this._annotationObservers.push(observer)
            })
        },

        observeViewer() {
            this.disconnectObserver()
            const viewer = this.$refs.viewer

            if (!viewer || typeof ResizeObserver === 'undefined') {
                return
            }

            this._resizeObserver = new ResizeObserver(() => {
                this.syncAnnotationLayers()
            })
            this._resizeObserver.observe(viewer)
        },

        disconnectObserver() {
            if (this._resizeObserver) {
                this._resizeObserver.disconnect()
                this._resizeObserver = null
            }
        },

        disconnectAnnotationObservers() {
            if (this._annotationObservers) {
                this._annotationObservers.forEach((observer) => observer.disconnect())
                this._annotationObservers = null
            }
        },

        resetViewer() {
            this.disconnectObserver()
            this.disconnectAnnotationObservers()
            this.src = null
        },
    },

    watch: {
        value: {
            handler(newValue) {
                if (this.src) {
                    this.resetViewer()
                }

                if (!newValue) {
                    return
                }

                setTimeout(() => { this.loadPdf(newValue); })
                
            },
            immediate: true
        }
    },

    beforeDestroy() {
        this.disconnectObserver()
        this.disconnectAnnotationObservers()
    },
}
</script>

<style lang="scss" scoped>
  .q-pdf-viewer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f5f3f4;

    &__page {
        &-wrapper {
            padding: 16px;
            width: 100%;
        }

        width: 100%;
        background-color: white;
        box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    }

    &__page-info {
        padding: 8px 24px;
        display: flex;
        justify-content: flex-end;
    }
  }
</style>
